import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES } from "@/data/services";

const IMG = {
  maison:
    "https://images.unsplash.com/photo-1637946175559-22c4fe13fc54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxibGFjayUyMGFic3RyYWN0JTIwbW9kZXJuJTIwc2hhcGVzfGVufDB8fHx8MTc5MTk3NDAyNXww&ixlib=rb-4.1.0&q=85",
  forge:
    "https://images.unsplash.com/photo-1708457753320-02e52a276a80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjByZWQlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzkxOTc0MDI1fDA&ixlib=rb-4.1.0&q=85",
  atlas:
    "https://images.unsplash.com/photo-1678366633407-7f49da199a42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxibGFjayUyMGFic3RyYWN0JTIwbW9kZXJuJTIwc2hhcGVzfGVufDB8fHx8MTc5MTk3NDAyNXww&ixlib=rb-4.1.0&q=85",
  positioning:
    "https://images.unsplash.com/photo-1569521597715-0e0c4e22e0e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxkYXJrJTIwYWJzdHJhY3QlMjByZWQlMjBnZW9tZXRyaWN8ZW58MHx8fHwxNzkxOTc0MDI1fDA&ixlib=rb-4.1.0&q=85",
};

const METHOD_STEPS = [
  { num: "01", title: "Discover", text: "We dig into your brand, your market and your objectives to understand exactly where you stand." },
  { num: "02", title: "Strategize", text: "We build a tailored strategy that connects your goals to the right channels and actions." },
  { num: "03", title: "Execute", text: "We deploy campaigns, content and assets with precision and discipline." },
  { num: "04", title: "Optimize", text: "We analyze performance, amplify what works and cut what doesn't." },
];

const CASES = [
  {
    id: "maison-noire",
    name: "Maison Noire",
    sector: "Premium Fashion",
    challenge:
      "A premium fashion label with a strong product, but an undefined digital presence and no clear positioning online.",
    strategy:
      "Brand positioning, visual identity direction, a content system and a launch campaign built around the label's universe.",
    img: IMG.maison,
    alt: "Abstract black architectural fins — concept visual for Maison Noire",
  },
  {
    id: "forge-fitness",
    name: "Forge Fitness",
    sector: "Fitness & Wellness",
    challenge:
      "A growing fitness brand competing in a crowded market, with inconsistent content and no real acquisition funnel.",
    strategy:
      "A performance content system, a paid acquisition structure and landing pages engineered for membership sign-ups.",
    img: IMG.forge,
    alt: "Abstract red and black geometric composition — concept visual for Forge Fitness",
  },
  {
    id: "atlas-consulting",
    name: "Atlas Consulting",
    sector: "B2B Services",
    challenge:
      "A B2B consulting firm relying entirely on referrals — invisible online and hard to differentiate from competitors.",
    strategy:
      "Authority positioning, a LinkedIn content strategy and a conversion-focused website with clear service pages.",
    img: IMG.atlas,
    alt: "Curved black ribbons on a dark background — concept visual for Atlas Consulting",
  },
];

const VALUES = ["Ambition", "Creativity", "Rigor", "Transparency", "Results"];

function Hero() {
  const reduce = useReducedMotion();
  const drift = (x, y, d) =>
    reduce ? {} : { animate: { x: [0, x, 0], y: [0, y, 0] }, transition: { duration: d, repeat: Infinity, ease: "easeInOut" } };

  return (
    <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-empire-black" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <motion.div className="halo-red pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full" aria-hidden="true" {...drift(60, -40, 22)} />
      <motion.div className="halo-red pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full opacity-80" aria-hidden="true" {...drift(-50, 50, 26)} />
      <div className="pointer-events-none absolute right-[12%] top-[22%] hidden h-40 w-40 animate-spin-slower border border-empire-red/30 lg:block" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-[14%] left-[8%] hidden h-24 w-24 animate-spin-slower-reverse rounded-full border border-white/10 lg:block" aria-hidden="true" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-10">
        <Reveal>
          <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-mist" data-testid="hero-overline">
            Digital Marketing · Brand Strategy · Growth
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1
            className="mt-6 font-anton text-5xl uppercase leading-[0.98] tracking-tight sm:text-7xl lg:text-8xl"
            data-testid="hero-title"
          >
            We build <span className="text-empire-red">brands.</span>
            <br />
            We create <span className="text-empire-red">growth.</span>
            <br />
            We build <span className="text-outline-red">empires.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-empire-mist sm:text-lg" data-testid="hero-subtitle">
            We help ambitious brands dominate their market through strategy, creativity and
            performance-driven marketing.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/services"
              data-testid="hero-explore-services-button"
              className="border border-white/20 px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-empire-red hover:text-empire-red"
            >
              Explore Our Services
            </Link>
            <Link
              to="/contact"
              data-testid="hero-start-project-button"
              className="bg-empire-red px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-empire-glow"
            >
              Start a Project
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section id="services" className="bg-empire-black py-24 lg:py-32" data-testid="home-services-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">What we do</p>
          <h2 className="mt-4 max-w-2xl font-anton text-4xl uppercase leading-tight sm:text-5xl" data-testid="services-heading">
            Services built for ambitious brands.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 4) * 0.07} className="h-full">
              <div
                className="group flex h-full flex-col border border-white/10 bg-empire-coal/40 p-8 transition-colors duration-300 hover:border-empire-red/60"
                data-testid={`service-card-${s.slug}`}
              >
                <s.icon className="h-7 w-7 text-empire-red" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="mt-6 font-anton text-lg uppercase tracking-wide">{s.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-empire-mist">{s.short}</p>
                <Link
                  to={`/services#${s.slug}`}
                  data-testid={`service-discover-${s.slug}`}
                  className="mt-6 inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-[0.18em] text-empire-red transition-colors group-hover:text-empire-glow"
                >
                  Discover More <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.21} className="h-full">
            <div className="flex h-full flex-col bg-empire-red p-8" data-testid="services-cta-card">
              <h3 className="font-anton text-xl uppercase leading-snug">Not sure where to start?</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-white/85">
                Tell us where your business stands. We&apos;ll point you to the right first move.
              </p>
              <Link
                to="/contact"
                data-testid="services-cta-button"
                className="mt-8 inline-flex w-fit items-center gap-2 bg-white px-6 py-3 font-grotesk text-xs uppercase tracking-[0.18em] text-empire-red transition-colors hover:bg-empire-black hover:text-white"
              >
                Let&apos;s Talk <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function MethodSection() {
  return (
    <section id="method" className="scroll-mt-24 bg-empire-coal py-24 lg:py-32" data-testid="method-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Our approach</p>
          <h2 className="mt-4 font-anton text-4xl uppercase leading-tight sm:text-5xl" data-testid="method-heading">
            A strategy built for growth.
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {METHOD_STEPS.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col bg-empire-coal p-8" data-testid={`method-step-${step.num}`}>
                <span className="font-anton text-6xl text-empire-red" aria-hidden="true">
                  {step.num}
                </span>
                <h3 className="mt-6 font-anton text-xl uppercase tracking-wide">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-empire-mist">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudies() {
  const [active, setActive] = useState(CASES[0].id);
  const current = CASES.find((c) => c.id === active);
  const reduce = useReducedMotion();

  return (
    <section id="work" className="scroll-mt-24 bg-empire-black py-24 lg:py-32" data-testid="case-studies-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Case studies</p>
          <h2 className="mt-4 font-anton text-4xl uppercase leading-tight sm:text-5xl" data-testid="case-studies-heading">
            Work in the making.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-empire-mist">
            Three concept projects that show how we think and work. Full results will be published
            here as soon as live client projects are available.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-3" role="tablist" aria-label="Concept projects">
            {CASES.map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={active === c.id}
                aria-controls={`case-panel`}
                data-testid={`case-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`border px-5 py-3 font-grotesk text-xs uppercase tracking-[0.18em] transition-colors ${
                  active === c.id
                    ? "border-empire-red bg-empire-red text-white"
                    : "border-white/15 text-empire-mist hover:border-empire-red/60 hover:text-white"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            id="case-panel"
            role="tabpanel"
            data-testid="case-panel"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? {} : { opacity: 0, y: -12 }}
            transition={{ duration: 0.35 }}
            className="mt-10 grid gap-8 border border-white/10 bg-empire-coal/40 p-6 sm:p-10 lg:grid-cols-2"
          >
            <div className="relative overflow-hidden">
              <img
                src={current.img}
                alt={current.alt}
                loading="lazy"
                className="h-64 w-full object-cover lg:h-full lg:min-h-[380px]"
              />
              <span className="absolute left-4 top-4 border border-empire-red bg-empire-black/80 px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] text-empire-red backdrop-blur">
                Concept project
              </span>
            </div>
            <div className="flex flex-col justify-center">
              <p className="font-grotesk text-xs uppercase tracking-[0.25em] text-empire-red">{current.sector}</p>
              <h3 className="mt-3 font-anton text-3xl uppercase" data-testid="case-title">{current.name}</h3>
              <div className="mt-6 space-y-5">
                <div>
                  <h4 className="font-grotesk text-xs uppercase tracking-[0.2em] text-white">The challenge</h4>
                  <p className="mt-2 text-sm leading-relaxed text-empire-mist">{current.challenge}</p>
                </div>
                <div>
                  <h4 className="font-grotesk text-xs uppercase tracking-[0.2em] text-white">Our strategy</h4>
                  <p className="mt-2 text-sm leading-relaxed text-empire-mist">{current.strategy}</p>
                </div>
                <p className="border-l-2 border-empire-red pl-4 text-sm italic text-empire-mist" data-testid="case-results-note">
                  Results will be published once a live project is available.
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function Positioning() {
  return (
    <section className="bg-empire-coal py-24 lg:py-32" data-testid="positioning-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <p className="font-grotesk text-xs uppercase tracking-[0.3em] text-empire-red">Why 4EMPIRE</p>
          <h2 className="mt-4 font-anton text-4xl uppercase leading-tight sm:text-5xl" data-testid="positioning-heading">
            Average is not an option.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-empire-mist">
            At 4EMPIRE, we believe that ambitious brands deserve more than ordinary marketing. We
            combine strategy, creativity and execution to help businesses build stronger brands,
            reach the right audience and create sustainable growth.
          </p>
          <ul className="mt-8 flex flex-wrap gap-3" data-testid="positioning-keywords">
            {VALUES.map((v) => (
              <li
                key={v}
                className="border border-white/15 px-4 py-2 font-grotesk text-xs uppercase tracking-[0.18em] text-white"
              >
                {v}
              </li>
            ))}
          </ul>
          <Link
            to="/about"
            data-testid="positioning-approach-button"
            className="mt-10 inline-flex items-center gap-2 border border-white/20 px-8 py-4 font-grotesk text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-empire-red hover:text-empire-red"
          >
            Discover the 4EMPIRE Approach <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative">
            <div className="halo-red pointer-events-none absolute -inset-8" aria-hidden="true" />
            <img
              src={IMG.positioning}
              alt="Abstract black and red composition representing the 4EMPIRE standard"
              loading="lazy"
              className="relative w-full border border-white/10 object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-empire-black py-28 lg:py-40" data-testid="final-cta-section">
      <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
      <div className="halo-red pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full" aria-hidden="true" />
      <div className="relative z-10 mx-auto max-w-5xl px-6 text-center lg:px-10">
        <Reveal>
          <h2 className="font-anton text-5xl uppercase leading-[0.95] sm:text-7xl" data-testid="final-cta-heading">
            Ready to build <span className="text-empire-red">your empire?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mx-auto mt-6 max-w-xl text-base text-empire-mist sm:text-lg">
            Let&apos;s turn your vision into a strategy built for growth.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <Link
            to="/contact"
            data-testid="final-cta-button"
            className="mt-10 inline-block bg-empire-red px-10 py-5 font-grotesk text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-empire-glow"
          >
            Start Your Project
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Seo
        title="4EMPIRE — Digital Marketing & Brand Growth Agency"
        description="4EMPIRE is a strategic marketing partner for ambitious brands across Europe. Strategy, creativity and performance-driven marketing that builds growth."
      />
      <Hero />
      <ServicesSection />
      <MethodSection />
      <CaseStudies />
      <Positioning />
      <FinalCta />
    </main>
  );
}
